import { Logger, useIntl, type UserRequirementStateStatus, useSetPageTitle, WarningRed } from '@capsicum/common';
import { SUMSUB_REDIRECTION_TIMEOUT, useSumsubRequirement } from '@capsicum/features/regulations/shared';
import { useUser } from '@capsicum/socios-core';
import { useEffect, useState } from 'react';

import { useIdentityVerificationModals } from './useIdentityVerificationModals';

import type { AnyEventName, EventPayload, PromisedTokenExpirationHandler, SnsError } from '@sumsub/websdk/types/types';

export type SumsubEventListeners = {
	onSdkAccessTokenExpiration: PromisedTokenExpirationHandler;
	onSdkError: (error: SnsError) => void;
	onSdkMessage: (type: AnyEventName, payload: EventPayload<AnyEventName>) => void;
};

type UseSumsubIdentityVerificationProps = {
	isUniqueRequirement: boolean;
	onDismiss: () => void;
	status?: UserRequirementStateStatus;
};

const END_OF_FLOW_EVENTS = ['idCheck.onApplicantSubmitted', 'idCheck.onApplicantResubmitted'] as Array<AnyEventName>;

export const useSumsubIdentityVerification = ({
	isUniqueRequirement,
	onDismiss,
	status
}: UseSumsubIdentityVerificationProps) => {
	const { $t } = useIntl();
	const {
		hideSumsubSdk,
		isCustomRequirement,
		isRequirementFulfilled,
		isRequirementLoading,
		submitIdentityVerification,
		sumsubCompletedForm
	} = useSumsubRequirement(status);

	const [redirectionTimeout, setRedirectionTimeout] = useState(null as NodeJS.Timeout | null);
	const [hasConsentedData, setHasConsentedData] = useState(false);
	const { refetchUser } = useUser();
	const {
		showContactCustomerSupportModal,
		showErrorModal,
		showExpiredVerificationModal,
		showIdentityVerificationRejectedTileModal,
		showIntroductoryModal,
		showUnderReviewModal
	} = useIdentityVerificationModals();

	useSetPageTitle($t('kycUserIdentity.verifyYourIdentity'));

	const triggerAutomaticRedirection = () => {
		if (isCustomRequirement) return;
		setRedirectionTimeout(
			setTimeout(() => {
				refetchUser();
				if (isUniqueRequirement) onDismiss();
			}, SUMSUB_REDIRECTION_TIMEOUT)
		);
	};

	const onSdkMessage = (type: AnyEventName, _: EventPayload<AnyEventName>) => {
		if (type && END_OF_FLOW_EVENTS.includes(type)) {
			submitIdentityVerification({ onSuccess: triggerAutomaticRedirection });
		}
	};

	const onSdkError = (error: SnsError) => {
		Logger.error(`Sumsub WebSDK error`, { error });

		showErrorModal({
			body: $t('kycUserIdentity.errorModal.description'),
			buttonText: $t('global.button.accept'),
			image: WarningRed,
			onDismiss,
			title: $t('kycUserIdentity.errorModal.title')
		});
	};

	const onSdkAccessTokenExpiration: SumsubEventListeners['onSdkAccessTokenExpiration'] = () => {
		triggerAutomaticRedirection();

		// Trigger expired error screen inside SDK by not sending a new token
		return new Promise(resolve => resolve(''));
	};

	useEffect(() => {
		return () => {
			if (redirectionTimeout) {
				clearTimeout(redirectionTimeout);
			}
		};
	}, [redirectionTimeout]);

	useEffect(() => {
		if (status === 'under_review' || status === 'waiting') {
			showUnderReviewModal(onDismiss);
		} else if (status === 'completed_deny') {
			showContactCustomerSupportModal(onDismiss, 'kycUserIdentity.retryModal.defaultTitle');
		} else if (status === 'retry') {
			showIdentityVerificationRejectedTileModal();
		} else if (status === 'expired') {
			showExpiredVerificationModal();
		} else if (status === undefined && !isUniqueRequirement) {
			showIntroductoryModal();
		}
	}, [status]);

	return {
		hasConsentedData,
		hideSumsubSdk,
		isLoading: isRequirementLoading,
		isRequirementFulfilled,
		listeners: { onSdkAccessTokenExpiration, onSdkError, onSdkMessage },
		setHasConsentedData,
		sumsubCompletedForm
	};
};
